/**
 * Toast Error Suppressor
 * Patches react-toastify to prevent removalReason errors
 * This is a workaround for react-toastify's internal error handling
 */

/**
 * Check if an error is toast-related
 */
const isToastError = (error) => {
  if (!error) return false;
  
  const errorMessage = String(error?.message || error?.toString() || "");
  const errorStack = String(error?.stack || "");
  
  return (
    errorMessage.includes("removalReason") ||
    errorMessage.includes("Cannot set properties of undefined") ||
    errorStack.includes("closeToast") ||
    errorStack.includes("removalReason")
  );
};

/**
 * Suppress toast errors globally by intercepting error events
 */
export const suppressToastErrors = () => {
  // Store original error handler
  const originalError = window.onerror;
  const originalUnhandledRejection = window.onunhandledrejection;

  // Override window.onerror to catch toast errors
  window.onerror = (message, source, lineno, colno, error) => {
    // Suppress toast-related errors
    if (isToastError(error || { message: String(message) })) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Toast error suppressed:", message);
      }
      return true; // Prevent default error handling
    }

    // Call original error handler for other errors
    if (originalError) {
      return originalError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Override unhandledrejection to catch promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;
    
    // Suppress toast-related errors
    if (isToastError(error)) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Toast promise rejection suppressed:", error?.message);
      }
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  }, true); // Use capture phase

  // Also patch React's error boundary reporting
  if (typeof window !== "undefined" && window.addEventListener) {
    window.addEventListener("error", (event) => {
      const error = event.error;
      
      // Suppress toast-related errors
      if (isToastError(error || { message: event.message })) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Toast error event suppressed:", event.message);
        }
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    }, true); // Use capture phase
  }

  // Suppress React DevTools error overlay for toast errors
  if (typeof window !== "undefined" && window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const originalOnError = window.__REACT_DEVTOOLS_GLOBAL_HOOK__?.onError;
    if (originalOnError) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onError = (error, errorInfo) => {
        if (!isToastError(error)) {
          originalOnError(error, errorInfo);
        } else if (process.env.NODE_ENV === "development") {
          console.warn("Toast error suppressed in React DevTools:", error?.message);
        }
      };
    }
  }
};

/**
 * Patch react-toastify's closeToast function by intercepting errors
 * This uses a safer approach that doesn't modify global Object methods
 */
const patchCloseToast = () => {
  if (typeof window === "undefined") return;

  try {
    // Instead of patching Object.defineProperty globally (which is risky),
    // we'll rely on the SafeToastContainer to intercept close button clicks
    // and the error suppression handlers to catch any remaining errors
    
    // This function is kept for future enhancements but the main work
    // is done by SafeToastContainer and error suppression handlers
    if (process.env.NODE_ENV === "development") {
      console.log("Toast close patching initialized (handled by SafeToastContainer)");
    }
  } catch (error) {
    console.error("Failed to initialize toast close patch:", error);
  }
};

/**
 * Initialize toast error suppression
 */
export const initializeToastErrorSuppression = () => {
  suppressToastErrors();
  patchCloseToast();
  console.log("Toast error suppression initialized");
};

